import { Component, signal } from '@angular/core';
import { deepmerge } from '@library/charts/utils';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { Home } from '@library/store/homes/homes.interface';
import { MeasuresFacade } from '@library/store/measures/measures.facade';
import { EnumApiScales, EnumMeasureTypes, TimeRange } from '@library/store/measures/measures.interface';
import { UserFacade } from '@library/store/user/user.facade';
import { getColorValue } from '@library/utils/helpers/functions/getColorVariable';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { ToDisplayValuePipe } from '@library/utils/pipes/to-display-value.pipe';
import { ToUserUnitPipe } from '@library/utils/pipes/to-user-unit.pipe';
import { GraphTooltipService } from '@molecules/graph-tooltip/graph-tooltip.service';
import { CpCompareGraphTooltipService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-body/cp-compare-graph/cp-compare-graph-tooltip.servcie';
import { CpCompareScaleService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-scale/cp-compare-scale.service';
import { CpCompareTimeRangeService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-timerange/cp-compare-timerange.service';
import { CpCompareSidebarService, MeasureCard, YScaleType } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { SPAN_GAPS, baseConfig } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/graph-configs/common-config';
import { AutoYMinMaxPlugin } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/plugins/auto-y-min-max';
import { VerticalLinePlugin } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/plugins/vertical-line';
import { DATASET_BASE_OPTIONS, SCALE_UNITS } from '@pages/control-page/control-page.const';
import { combineLatest, debounceTime, firstValueFrom, map, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'app-cp-compare-graph',
  templateUrl: './cp-compare-graph.component.html',
  styleUrls: ['./cp-compare-graph.component.scss'],
  providers: [
    {provide: GraphTooltipService, useClass: CpCompareGraphTooltipService}
  ],
})
export class CpCompareGraphComponent {

  hasNoData = signal(false);

  private config$ = combineLatest({
    measureCards: this.compareSidebarService.measureCards$,
    apiScale: this.compareScaleService.apiScale$,
    firstTimeRange: this.compareTimeRangeService.firstTimeRange$,
    secondTimeRange: this.compareTimeRangeService.secondTimeRange$,
    home: this.homesFacade.currentHome$.pipe(isDefined()),
  }).pipe(
    switchMap(({measureCards, apiScale, home, secondTimeRange}) => this.buildConfig(home, measureCards, apiScale, secondTimeRange)),
  );

  private bound$ = combineLatest({
    firstTimeRange: this.compareTimeRangeService.firstTimeRange$,
    secondTimeRange: this.compareTimeRangeService.secondTimeRange$,
  }).pipe(
    map(({firstTimeRange, secondTimeRange}) => {
      const bounds = {
        'xCurrent': {
          min: firstTimeRange.dateBegin.valueOf(),
          max: firstTimeRange.dateEnd.valueOf(),
        },
      };
      if (secondTimeRange) {
        bounds['xPrevious'] = {
          min: secondTimeRange.dateBegin.valueOf(),
          max: secondTimeRange.dateEnd.valueOf(),
        };
      }

      return bounds;
    }),
  );

  private loading$ = this.homesFacade.currentHomeId$.pipe(
    switchMap(() => combineLatest({
      measureCards: this.compareSidebarService.measureCards$,
      apiScale: this.compareScaleService.apiScale$,
    })),
    switchMap(({measureCards, apiScale}) => combineLatest(measureCards.map(d => {
      if (d.home) {
        return this.measuresFacade.getHomeLoadStatus(d.home.id, apiScale);
      }
      else if (d.module) {
        return this.measuresFacade.getModuleLoadStatus(d.module.id, apiScale);
      }
      else if (d.room) {
        return this.measuresFacade.getRoomLoadStatus(d.room.id, apiScale);
      }
    }) ?? [])),
    map(datasets => datasets.some(loading => loading)),
    startWith(false),
  );

  viewModel$ = combineLatest({
    loading: this.loading$,
    config: this.config$,
    apiScale: this.compareScaleService.apiScale$,
    firstTimeRange: this.compareTimeRangeService.firstTimeRange$,
    bounds: this.bound$,
    measureCards: this.compareSidebarService.measureCards$,
  }).pipe(debounceTime(200));

  constructor(
    private compareSidebarService: CpCompareSidebarService,
    private compareTimeRangeService: CpCompareTimeRangeService,
    private compareScaleService: CpCompareScaleService,
    private measuresFacade: MeasuresFacade,
    private homesFacade: HomesFacade,
    private graphTooltipService: GraphTooltipService,
    private userFacade: UserFacade,
    private toDisplayValuePipe: ToDisplayValuePipe,
    private toUserUnitPipe: ToUserUnitPipe,
  ) { }

  buildConfig(home: Home, measureCards: MeasureCard[], apiScale: EnumApiScales, secondTimeRange: TimeRange) {
    const moduleCards = measureCards.filter(m => m.module);
    const roomCards = measureCards.filter(m => m.room);

    return combineLatest({
      homeMeasures: this.measuresFacade.getHomeMeasures(home),
      modulesMeasures: this.measuresFacade.getModulesMeasures(moduleCards.map(m => m.module)),
      roomsMeasures: this.measuresFacade.getRoomsMeasures(roomCards.map(m => m.room)),
    }).pipe(
      switchMap(async ({ homeMeasures, modulesMeasures, roomsMeasures}) => {

        const scale = await firstValueFrom(this.compareScaleService.scale$);

        const datasets = measureCards
          .map((measureCard) => {

            let data = [];
            if (measureCard.home) {
              data = homeMeasures[measureCard.measureType]?.[apiScale] ?? [];
            }
            else if (measureCard.module) {
              data = modulesMeasures[moduleCards.indexOf(measureCard)][measureCard.measureType]?.[apiScale] ?? [];
            }
            else if (measureCard.room) {
              data = roomsMeasures[roomCards.indexOf(measureCard)][measureCard.measureType]?.[apiScale] ?? [];
            }

            data = data.map(m => ({...m, y: this.toUserUnitPipe.transform(m.y, measureCard.measureType)}));

            return {
              ...DATASET_BASE_OPTIONS[measureCard.type],
              data: data,
              yAxisID: measureCard.yScaleType,
              borderColor: getColorValue(measureCard.color),
              backgroundColor: getColorValue(measureCard.color),
              spanGaps: SPAN_GAPS[apiScale],
              xAxisID: 'xCurrent',
              order: measureCard.type.includes('bar') ? 1 : 0,
            };
          });

        this.hasNoData.set(datasets.every(d => d.data?.length === 0));

        if (secondTimeRange) {
          datasets.push(
            ...datasets.map((d, i) => ({
              ...d, xAxisID: 'xPrevious',
              borderColor: hexToRgba(datasets[i].borderColor, 0.5),
              backgroundColor: hexToRgba(datasets[i].backgroundColor, 0.5),
              order: datasets[i].type.includes('bar') ? 1 : 0,
            })),
          );
        }

        const user = await firstValueFrom(this.userFacade.currentUser$);
        const currencySymbol = await firstValueFrom(this.homesFacade.currencySymbol$);

        const config = deepmerge(baseConfig, {
          data: {
            datasets: datasets,
          },
          options: {
            scales: {
              xCurrent: {type: scale, offset: false},
            },
            plugins: {
              tooltip: this.graphTooltipService.tooltip,
              'auto-y-min-max': {
                unitSystem: user.unit_system ?? 0,
                unitPressure: user.unit_pressure ?? 0,
                unitWind: user.unit_wind ?? 0,
              }
            }
          },
          plugins: [AutoYMinMaxPlugin, VerticalLinePlugin]
        });

        if (secondTimeRange) {
          config.options.scales.xPrevious = {type: scale, offset: false, display: false};
        }

        const toDisplayValuePipe = this.toDisplayValuePipe;

        const yScales = [...new Set(measureCards.map(m => m.yScaleType))];
        yScales.forEach((type, i) => {
          config.options.scales[type] = {
            type: type,
            position: i % 2 === 0 ? 'left' : 'right',
            grid: { drawOnChartArea: i === 0 },
            ticks: {
              callback: function (val) {
                if (type === YScaleType.ELECTRICITY) {
                  const value = toDisplayValuePipe.transform(val, EnumMeasureTypes.SUM_ENERGY_ELEC_BASE);
                  return `${value} ${SCALE_UNITS[this.type](user, currencySymbol)}`;
                }
                return `${this.getLabelForValue(val)} ${SCALE_UNITS[this.type](user, currencySymbol)}`;
              }
            }
          };
        });

        delete config.options.scales.x;
        delete config.options.scales.y;

        return config;
      }),
    );
  }

}

function hexToRgba(hex: string, transparency: number) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${transparency})`
    : null;
}